import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { globalSearch, SearchResult } from "../../../services/searchServices/GlobalSearchService";
import { Search, Loader2, ClipboardList, User, Users } from "lucide-react"; 
import './GlobalSearch.css';

const GlobalSearch: React.FC = () => {
    const { currentUser, currentOrganization } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

    const [toggleSearch, setToggleSearch] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [filteredResults, setFilteredResults] = useState<SearchResult[]>([]);
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const displayedResults = filteredResults.slice(0, 10);

    // ... Keep all standard useEffect hooks and handleNavigation functions exactly the same ...
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredResults([]);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            if (currentUser) {
                setIsSearching(true);
                try {
                    const authToken = await currentUser.getIdToken();
                    const results = await globalSearch(authToken, searchTerm);
                    setFilteredResults(results);
                } catch (error) {
                    console.error("Failed to search:", error);
                    setFilteredResults([]);
                } finally {
                    setIsSearching(false);
                }
            }
        }, 400);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, currentUser]);

    useEffect(() => {
        setHoveredIndex(filteredResults.length > 0 ? 0 : -1);
    }, [filteredResults]);

    const handleNavigation = (result: SearchResult) => {
        setToggleSearch(false);
        setSearchTerm("");
        setHoveredIndex(-1);
        if (result.type === 'task') {
            navigate(`/task/${currentOrganization?.abbreviation}-${result.friendlyId}`);
        } else if (result.type === 'participant') {
            navigate(`/drive/caseFileDrive/${result.id}/info`);
        } else if (result.type === 'employee') {
            navigate(`/profile/user/${result.id}`);
        }
    };

    useEffect(() => {
        setToggleSearch(false);
        setSearchTerm("");
    }, [location]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const clickedInsideSearch = searchRef.current && searchRef.current.contains(event.target as Node);
            const clickedInsideResults = resultsRef.current && resultsRef.current.contains(event.target as Node);

            if (!clickedInsideSearch && !clickedInsideResults) {
                setToggleSearch(false);
                setSearchTerm("");
                setHoveredIndex(-1);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHoveredIndex((prev) => prev < displayedResults.length - 1 ? prev + 1 : 0);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHoveredIndex((prev) => prev > 0 ? prev - 1 : displayedResults.length - 1);
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (hoveredIndex !== -1 && displayedResults[hoveredIndex]) {
                handleNavigation(displayedResults[hoveredIndex]);
            }
        }
    };

    const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const getHighlightedText = (text: string, highlight: string) => {
        if (!highlight.trim()) return <span>{text}</span>;
        const safeHighlight = escapeRegExp(highlight);
        const parts = text.split(new RegExp(`(${safeHighlight})`, 'gi'));
        return (
            <span>
                {parts.map((part, i) =>
                    part.toLowerCase() === highlight.toLowerCase() ? (
                        <span key={i} className="bg-purple-100 dark:bg-purple-400/20 text-[#7E57C2] dark:text-purple-300 px-0.5 rounded font-bold">
                            {part}
                        </span>
                    ) : (
                        part
                    )
                )}
            </span>
        );
    };

    const renderResult = (result: SearchResult, index: number) => {
        let IconDisplay: React.ReactNode;
        let title = '';
        let description = '';
        let badge: React.ReactNode = null;
        let isMuted = false;

        switch (result.type) {
            case 'task':
                IconDisplay = <div className="p-1.5 bg-purple-50 dark:bg-purple-400/15 rounded-md text-[#7E57C2] dark:text-purple-300"><ClipboardList className="w-4 h-4" /></div>;
                title = `[${currentOrganization?.abbreviation}-${result.friendlyId}] ${result.title}`;
                description = `${result.taskContext === 'Employee' ? 'Employee' : 'Participant'}: ${result.subjectName || 'Unknown'}`;
                
                if (result.isDeleted) {
                    badge = <span className="global-search-status global-search-status--deleted">Deleted</span>;
                    isMuted = true;
                } else if (result.statusName?.toLowerCase() === 'completed') {
                    badge = <span className="global-search-status global-search-status--completed">Completed</span>;
                    isMuted = true;
                }
                break;
            case 'participant':
                IconDisplay = <div className="p-1.5 bg-blue-50 dark:bg-blue-400/15 rounded-md text-blue-600 dark:text-blue-300"><User className="w-4 h-4" /></div>;
                title = `${result.firstName} ${result.lastName}`;
                description = `Region ${result.region} - ${result.waiver}`;
                
                if (result.deactivated) {
                    badge = <span className="global-search-status global-search-status--inactive">Deactivated</span>;
                    isMuted = true;
                }
                break;
            case 'employee':
                IconDisplay = <div className="p-1.5 bg-emerald-50 dark:bg-emerald-400/15 rounded-md text-emerald-600 dark:text-emerald-300"><Users className="w-4 h-4" /></div>;
                title = `${result.firstName} ${result.lastName}`;
                description = result.email;
                
                if (result.deactivated) {
                    badge = <span className="global-search-status global-search-status--inactive">Deactivated</span>;
                    isMuted = true;
                }
                break;
        }

        return (
            <div
                key={`${result.type}-${result.id}`}
                className={`global-search-result flex items-start gap-2.5 p-2 rounded-lg transition-colors cursor-pointer ${
                    index === hoveredIndex ? 'bg-[#F9F6FF] dark:bg-[#282C39]' : 'hover:bg-gray-50 dark:hover:bg-[#282C39]'
                } ${isMuted ? 'global-search-result--inactive' : ''}`}
                onMouseDown={(e) => {
                    e.preventDefault();
                    handleNavigation(result);
                }}
                onMouseEnter={() => setHoveredIndex(index)}
            >
                <div className={`shrink-0 mt-0.5 ${isMuted ? 'grayscale' : ''}`}>{IconDisplay}</div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100 truncate m-0 leading-tight">
                            {getHighlightedText(title, searchTerm)}
                        </h3>
                        {badge}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 truncate m-0 mt-0.5">
                        {description}
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div ref={searchRef} className="relative z-[100] w-full max-w-[400px]">
            {/* Search Input Bar */}
            <div className="relative flex items-center w-full">
                <Search className="absolute left-3 text-gray-400 w-4 h-4" />
                <input
                    ref={inputRef}
                    type="search"
                    placeholder="Search anything..."
                    className="dark-surface-muted dark-input-borderless w-full bg-white dark:bg-zinc-800 border border-gray-200 text-gray-900 dark:text-zinc-100 text-sm rounded-lg block pl-9 pr-3 py-1.5 md:py-2 h-8 md:h-9 transition-all shadow-sm dark:shadow-none outline-none placeholder:text-gray-400 dark:placeholder:text-zinc-500 [&::-webkit-search-cancel-button]:hidden"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        if (!toggleSearch) setToggleSearch(true);
                    }}
                    onKeyDown={handleKeyDown}
                    onClick={() => setToggleSearch(true)}
                />
            </div>

            {/* Dropdown Results Panel */}
            <div 
                ref={resultsRef}
                className={`dark-popup absolute top-[110%] left-0 w-full min-w-[320px] bg-white dark:bg-zinc-900 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_16px_40px_rgba(0,0,0,0.4)] border border-gray-100 p-1.5 origin-top transition-all duration-200 ease-out
                ${toggleSearch && searchTerm.trim() !== "" ? 'scale-100 opacity-100 translate-y-0 visible' : 'scale-95 opacity-0 -translate-y-2 invisible'}`}
            >
                {isSearching ? (
                    <div className="flex flex-col items-center justify-center py-6 text-gray-400 gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-[#7E57C2]" />
                        <span className="text-xs font-medium">Searching records...</span>
                    </div>
                ) : (
                    <div className="theme-scrollbar flex flex-col gap-0.5 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200">
                        {displayedResults.map((result, index) => renderResult(result, index))}

                        {searchTerm.trim() !== "" && !isSearching && filteredResults.some(r => r.type === 'task') && (
                            <button
                                className="w-full mt-1.5 py-2 px-3 text-center text-sm font-semibold text-[#7E57C2] dark:text-purple-300 bg-purple-50 dark:bg-purple-400/10 hover:bg-purple-100 dark:hover:bg-purple-400/20 rounded-lg transition-colors border-none cursor-pointer"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setToggleSearch(false);
                                    setSearchTerm("");
                                    navigate(`/tasks?searchTerm=${searchTerm.trim()}`);
                                }}
                            >
                                View all matching tasks →
                            </button>
                        )}
                        
                        {searchTerm.trim() !== "" && !isSearching && filteredResults.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-6 text-center bg-gray-50 dark:bg-[#20232E] rounded-lg">
                                <Search className="w-6 h-6 text-gray-300 mb-2" />
                                <span className="text-sm font-semibold text-gray-700 dark:text-slate-200">No results found</span>
                                <span className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Try adjusting your search term</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GlobalSearch;
